import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../../supabase-client";
import { hashPassword } from "../../utils/hashPassword"; // Use the utility here
import { Eye, EyeOff, Mail, Lock, User, Phone } from "lucide-react";
// import { set } from "react-hook-form";

const Register = () => {
   const [codeDigits, setCodeDigits] = useState(["", "", "", "", "", ""]);
   const codeInputRefs = useRef([]);

   const [claveIngresada, setClaveIngresada] = useState("");
   const [claveGenerada, setClaveGenerada] = useState("");
   const [formData, setFormData] = useState({
      email: "",
      password: "",
      confirmPassword: "",
      nombre: "",
      celular: "",
   });
   const [showPassword, setShowPassword] = useState(false);
   const [showConfirmPassword, setShowConfirmPassword] = useState(false);
   const [isLoading, setIsLoading] = useState(false);
   const [error, setError] = useState("");
   const [success, setSuccess] = useState("");
   const [showModal, setShowModal] = useState(false);
   const navigate = useNavigate();

   useEffect(() => {
      codeInputRefs.current = codeInputRefs.current.slice(0, 6);
   }, []);

   // Check if user is already logged in
   useEffect(() => {
      const checkAuth = async () => {
         const {
            data: { session },
         } = await supabase.auth.getSession();
         if (session) {
            navigate("/"); // Redirect to home if already logged in
         }
      };
      checkAuth();
   }, [navigate]);

   const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
         ...prev,
         [name]: value,
      }));

      // Clear errors when user starts typing
      if (error) setError("");
   };

   const validateForm = () => {
      const { email, password, confirmPassword, nombre, celular } = formData;

      if (!email || !password || !confirmPassword || !nombre || !celular) {
         setError("Por favor completa todos los campos requeridos");
         return false;
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
         setError("Por favor ingresa un email válido");
         return false;
      }

      if (password.length < 6) {
         setError("La contraseña debe tener al menos 6 caracteres");
         return false;
      }

      if (password !== confirmPassword) {
         setError("Las contraseñas no coinciden");
         return false;
      }

      if (!/^\+?[\d\s-()]+$/.test(celular)) {
         setError("Por favor ingresa un número de teléfono válido");
         return false;
      }

      return true;
   };

   const handleRegister = async (e) => {
      e.preventDefault();

      if (!validateForm()) return;

      setIsLoading(true);
      setError("");
      setSuccess("");

      try {
         // 1. Look if email is not registered
         const { data: userData, error: userError } = await supabase
            .from("usuarios")
            .select("*")
            .eq("email", formData.email.trim())
            .single();

         if (userError) {
            console.log('userError: ', userError);
         }

         if (!userData) {
            // 2. Insert additional user data into usuarios table
            const hashedPassword = await hashPassword(formData.password);
            var nuevaClave = Math.floor(100000 + Math.random() * 900000);
            setClaveGenerada(nuevaClave);

            const { error: dbError } = await supabase.from("usuarios").insert([
               {
                  email: formData.email.trim(),
                  nombre: formData.nombre,
                  celular: formData.celular,
                  documento: 1,
                  password_hash: hashedPassword,
                  perfil: 28, // Email no confirmado
                  created_at: new Date().toISOString(),
               },
            ]);

            if (dbError) {
               console.error("Database error:", dbError);
               // Continue even if database insert fails, as auth user was created
               alert(
                  "Error al guardar datos adicionales. Por favor contacta al soporte."
               );
               return;
            }

            // Send email confirmation
            const resp = await fetch(
               "https://coympvieahjboblypoft.supabase.co/functions/v1/mail-register",
               {
                  method: "POST",
                  headers: {
                     "Content-Type": "application/json",
                  },
                  body: JSON.stringify({
                     aquien: formData.email.trim(), // Mail Adminstrador
                     asunto: "Registro exitoso en Diego Curutchet",
                     mensaje: `<p>${formData.nombre} gracias por registrarte en Diego Curutchet.</p>
                               <p> Tu cuenta ha sido creada exitosamente.</p>
                               <p>Tu clave de acceso temporal es: <b>${nuevaClave}</b>.</p>
                               <p>Por favor, inicia sesión y actualiza tu perfil con esta clave para activar tu cuenta.</p>`,
                  }),
               }
            );
            
            if (!resp.ok) {
               console.error("Error sending confirmation email");
            }

            setShowModal(true);
            // 3. Show success message
         } else {
            setError("Este email ya está registrado. ¿Quieres iniciar sesión?");
            setSuccess("");
            setFormData((prev) => ({ ...prev, email: "" })); // Clear email field
            setIsLoading(false);
            return;
         }
      } catch (error) {
         console.error("Registration error:", error);

         // Handle specific error messages
         switch (error.message) {
            case "User already registered":
               setError(
                  "Este email ya está registrado. ¿Quieres iniciar sesión?"
               );
               break;
            case "Password should be at least 6 characters":
               setError("La contraseña debe tener al menos 6 caracteres");
               break;
            case "Invalid email":
               setError("Por favor ingresa un email válido");
               break;
            default:
               setError(error.message || "Error al crear la cuenta");
         }
      } finally {
         setIsLoading(false);
      }
   };

   // Handle individual digit change
   const handleDigitChange = (index, value) => {
      // Only allow single digit
      if (value.length > 1) return;

      // Only allow numbers
      if (!/^\d*$/.test(value)) return;

      const newDigits = [...codeDigits];
      newDigits[index] = value;
      setCodeDigits(newDigits);

      // Update claveIngresada with complete code
      setClaveIngresada(newDigits.join(""));

      // Auto-focus next input
      if (value && index < 5) {
         codeInputRefs.current[index + 1]?.focus();
      }
   };

   // Handle backspace/delete
   const handleKeyDown = (index, e) => {
      if (e.key === "Backspace" && !codeDigits[index] && index > 0) {
         // Focus previous input on backspace if current is empty
         codeInputRefs.current[index - 1]?.focus();
      }
   };

   // Handle paste event
   const handlePaste = (e) => {
      e.preventDefault();
      const paste = e.clipboardData.getData("text");

      // Only process if it's exactly 6 digits
      if (/^\d{6}$/.test(paste)) {
         const digits = paste.split("");
         setCodeDigits(digits);
         setClaveIngresada(paste);

         // Focus the last input
         codeInputRefs.current[5]?.focus();
      }
   };
// Updated handleclaveAcceso function
const handleclaveAcceso = () => {
  const enteredCode = codeDigits.join('');
  
  if (enteredCode.length !== 6) {
    alert('Por favor ingresa todos los 6 dígitos');
    return;
  }
  
  console.log("claveGenerada: ", claveGenerada);
  console.log("claveIngresada: ", enteredCode);
  
  if (enteredCode === claveGenerada.toString()) {
    // Update user profile to confirmed (perfil = 18)
    updateUserProfile();
    setShowModal(false);
    setSuccess("¡Cuenta activada exitosamente! Ya puedes iniciar sesión.");
    
    // Redirect to login
    setTimeout(() => {
      navigate('/login');
    }, 2000);
  } else {
    alert('Código incorrecto. Por favor verifica el código enviado a tu email.');
    // Clear inputs and focus first one
    setCodeDigits(['', '', '', '', '', '']);
    setClaveIngresada('');
    codeInputRefs.current[0]?.focus();
  }
};

// Add function to update user profile
const updateUserProfile = async () => {
  try {
    const { error } = await supabase
      .from('usuarios')
      .update({ perfil: 18 }) // Change from 28 (unconfirmed) to 18 (confirmed)
      .eq('email', formData.email.trim());
    
    if (error) {
      console.error('Error updating user profile:', error);
    }
  } catch (error) {
    console.error('Error in updateUserProfile:', error);
  }
};


   return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center px-4 py-16">
         <div className="w-full max-w-sm">
            {/* Header */}
            <div className="text-center mb-10">
               <h1
                  className="text-[3rem] font-medium text-[#1A1A1A] mb-1"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
               >
                  Crear Cuenta
               </h1>
               <p className="text-[0.68rem] uppercase tracking-[0.2em] text-[#7A7570]">Únete a Diego Curutchet</p>
               <div className="w-8 h-[1px] bg-[#9B6D3F] mx-auto mt-5" />
            </div>

            {/* Alerts */}
            {error && (
               <div className="mb-6 px-4 py-3 bg-red-50 border-l-2 border-red-500 text-sm text-red-700">
                  {error}
               </div>
            )}
            {success && (
               <div className="mb-6 px-4 py-3 bg-emerald-50 border-l-2 border-emerald-500 text-sm text-emerald-700">
                  {success}
               </div>
            )}

            {/* Form */}
            <form onSubmit={handleRegister} className="space-y-6">
               {/* Email */}
               <div>
                  <label htmlFor="email" className="block text-[0.68rem] uppercase tracking-[0.15em] text-[#7A7570] mb-2">
                     Email *
                  </label>
                  <div className="relative">
                     <Mail className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9B6D3F]" />
                     <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full pl-6 pb-2 bg-transparent border-b border-[#EDE9E4] focus:border-[#9B6D3F] outline-none text-sm text-[#1A1A1A] transition-colors placeholder-[#C5C0BB]"
                        placeholder="tu@email.com"
                     />
                  </div>
               </div>

               {/* Name */}
               <div>
                  <label htmlFor="nombre" className="block text-[0.68rem] uppercase tracking-[0.15em] text-[#7A7570] mb-2">
                     Nombre completo *
                  </label>
                  <div className="relative">
                     <User className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9B6D3F]" />
                     <input
                        id="nombre"
                        name="nombre"
                        type="text"
                        required
                        value={formData.nombre}
                        onChange={handleInputChange}
                        className="w-full pl-6 pb-2 bg-transparent border-b border-[#EDE9E4] focus:border-[#9B6D3F] outline-none text-sm text-[#1A1A1A] transition-colors placeholder-[#C5C0BB]"
                        placeholder="Tu nombre completo"
                     />
                  </div>
               </div>

               {/* Phone */}
               <div>
                  <label htmlFor="celular" className="block text-[0.68rem] uppercase tracking-[0.15em] text-[#7A7570] mb-2">
                     Teléfono *
                  </label>
                  <div className="relative">
                     <Phone className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9B6D3F]" />
                     <input
                        id="celular"
                        name="celular"
                        type="tel"
                        required
                        value={formData.celular}
                        onChange={handleInputChange}
                        className="w-full pl-6 pb-2 bg-transparent border-b border-[#EDE9E4] focus:border-[#9B6D3F] outline-none text-sm text-[#1A1A1A] transition-colors placeholder-[#C5C0BB]"
                        placeholder="+54 11 1234-5678"
                     />
                  </div>
               </div>

               {/* Password */}
               <div>
                  <label htmlFor="password" className="block text-[0.68rem] uppercase tracking-[0.15em] text-[#7A7570] mb-2">
                     Contraseña *
                  </label>
                  <div className="relative">
                     <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9B6D3F]" />
                     <input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        required
                        value={formData.password}
                        onChange={handleInputChange}
                        className="w-full pl-6 pr-7 pb-2 bg-transparent border-b border-[#EDE9E4] focus:border-[#9B6D3F] outline-none text-sm text-[#1A1A1A] transition-colors placeholder-[#C5C0BB]"
                        placeholder="Mínimo 6 caracteres"
                     />
                     <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-0 top-1/2 -translate-y-1/2 text-[#9B6D3F] hover:text-[#6B4A2A] transition-colors"
                     >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                     </button>
                  </div>
               </div>

               {/* Confirm Password */}
               <div>
                  <label htmlFor="confirmPassword" className="block text-[0.68rem] uppercase tracking-[0.15em] text-[#7A7570] mb-2">
                     Confirmar contraseña *
                  </label>
                  <div className="relative">
                     <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9B6D3F]" />
                     <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        required
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        className="w-full pl-6 pr-7 pb-2 bg-transparent border-b border-[#EDE9E4] focus:border-[#9B6D3F] outline-none text-sm text-[#1A1A1A] transition-colors placeholder-[#C5C0BB]"
                        placeholder="Repite tu contraseña"
                     />
                     <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-0 top-1/2 -translate-y-1/2 text-[#9B6D3F] hover:text-[#6B4A2A] transition-colors"
                     >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                     </button>
                  </div>
               </div>

               {/* Submit */}
               <div className="pt-2">
                  <button
                     type="submit"
                     disabled={isLoading}
                     className="w-full py-3.5 bg-[#9B6D3F] text-white text-[0.72rem] uppercase tracking-[0.18em] hover:bg-[#6B4A2A] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                     {isLoading ? (
                        <>
                           <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                           Creando cuenta...
                        </>
                     ) : (
                        "Crear Cuenta"
                     )}
                  </button>
               </div>
            </form>

            {/* Login link */}
            <div className="mt-8 text-center border-t border-[#EDE9E4] pt-6">
               <p className="text-sm text-[#7A7570]">
                  ¿Ya tienes cuenta?{" "}
                  <Link to="/login" className="text-[#9B6D3F] hover:text-[#6B4A2A] transition-colors underline underline-offset-2">
                     Iniciar sesión
                  </Link>
               </p>
            </div>
         </div>

         {/* Verification Modal */}
         {showModal && (
            <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/60">
               <div className="bg-white p-8 max-w-sm w-full mx-4 shadow-2xl">
                  <h2
                     className="text-2xl font-medium text-[#1A1A1A] mb-3 text-center"
                     style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                  >
                     Verificá tu email
                  </h2>
                  <p className="text-sm text-[#7A7570] mb-7 text-center leading-relaxed">
                     Enviamos un código de 6 dígitos a tu email. Ingresalo para activar tu cuenta.
                  </p>

                  {/* Code input */}
                  <div className="flex justify-center gap-2 mb-7">
                     {codeDigits.map((digit, index) => (
                        <input
                           key={index}
                           ref={(el) => (codeInputRefs.current[index] = el)}
                           type="text"
                           value={digit}
                           onChange={(e) => handleDigitChange(index, e.target.value)}
                           onKeyDown={(e) => handleKeyDown(index, e)}
                           onPaste={index === 0 ? handlePaste : undefined}
                           className="w-11 h-12 text-center text-lg font-medium border-b-2 border-[#EDE9E4] focus:border-[#9B6D3F] outline-none bg-[#FAFAF8] text-[#1A1A1A] transition-colors"
                           maxLength="1"
                           autoComplete="off"
                        />
                     ))}
                  </div>

                  <div className="space-y-2">
                     <button
                        onClick={handleclaveAcceso}
                        disabled={claveIngresada.length !== 6}
                        className="w-full py-3 bg-[#9B6D3F] text-white text-[0.72rem] uppercase tracking-[0.15em] hover:bg-[#6B4A2A] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                     >
                        Confirmar
                     </button>
                     <button
                        onClick={() => {
                           setShowModal(false);
                           setCodeDigits(["", "", "", "", "", ""]);
                           setClaveIngresada("");
                        }}
                        className="w-full py-2.5 text-[0.72rem] uppercase tracking-[0.12em] text-[#7A7570] hover:text-[#1A1A1A] transition-colors border border-[#EDE9E4] hover:border-[#1A1A1A]"
                     >
                        Cancelar
                     </button>
                  </div>
               </div>
            </div>
         )}
      </div>
   );
};

export default Register;
